import "@/index.css";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LangProvider } from "@/context/LangContext";
import { Toaster } from "sonner";
import LandingPage from "@/pages/LandingPage";
import DashboardPage from "@/pages/DashboardPage";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import OnboardingPage from "@/pages/OnboardingPage";
import PricingPage from "@/pages/PricingPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import AgentSetupPage from "@/pages/AgentSetupPage";
import OmniPlanPage from "@/pages/OmniPlanPage";
import AlertsPage from "@/pages/AlertsPage";
import OAuthCallbackPage from "@/pages/OAuthCallbackPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import NotFoundPage from "@/pages/NotFoundPage";
import BillingPage from "@/pages/BillingPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import ConnectionsPage from "@/pages/ConnectionsPage";
import ConnectorCallbackPage from "@/pages/ConnectorCallbackPage";
import IntelligenceSettingsPage from "@/pages/IntelligenceSettingsPage";
import { Loader2 } from "lucide-react";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 size={28} className="animate-spin" style={{ color: '#00D4FF' }} />
    </div>
  );
  if (!user) return <Navigate to="/auth" replace />;
  return children;
}

function GuestRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 size={28} className="animate-spin" style={{ color: '#00D4FF' }} />
    </div>
  );
  if (user) {
    if (!user.onboarding_complete) return <Navigate to="/onboarding" replace />;
    return <Navigate to="/dashboard/all-brands/command-center" replace />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Check URL fragment for session_id synchronously (Google OAuth callback)
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<GuestRoute><AuthPage /></GuestRoute>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<Navigate to="/dashboard/all-brands/command-center" replace />} />
      <Route path="/dashboard/:brandSlug" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/dashboard/:brandSlug/:tab" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/pricing" element={<ProtectedRoute><PricingPage /></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
      <Route path="/agent/setup" element={<ProtectedRoute><AgentSetupPage /></ProtectedRoute>} />
      <Route path="/omniplan" element={<ProtectedRoute><OmniPlanPage /></ProtectedRoute>} />
      <Route path="/alerts" element={<ProtectedRoute><AlertsPage /></ProtectedRoute>} />
      <Route path="/oauth/callback" element={<ProtectedRoute><OAuthCallbackPage /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
      <Route path="/connections" element={<ProtectedRoute><ConnectionsPage /></ProtectedRoute>} />
      <Route path="/settings/intelligence" element={<ProtectedRoute><IntelligenceSettingsPage /></ProtectedRoute>} />
      <Route path="/connections/callback" element={<ProtectedRoute><ConnectorCallbackPage /></ProtectedRoute>} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <LangProvider>
        <AuthProvider>
          <BrowserRouter>
            <a href="#main" className="skip-link">Skip to main content</a>
            <Toaster position="bottom-right" richColors closeButton />
            <AppRouter />
          </BrowserRouter>
        </AuthProvider>
      </LangProvider>
    </ThemeProvider>
  );
}

export default App;
